// Street furniture: light poles along the pavements. Heads are emissive
// meshes whose intensity the stage raises at night (v1 street lights used
// real PointLights; v2 keeps it to emissive + night glow for mobile perf).

import * as THREE from 'three';
import type { Rng } from '@/core/rng';
import { InstancedSet } from '@/render/instanced-set';

const TILE_LENGTH = 80;
const POLE_PITCH = 16;

const poleGeo = new THREE.CylinderGeometry(0.05, 0.07, 2.6, 6);
const headGeo = new THREE.SphereGeometry(0.14, 8, 6);

const poleMat = new THREE.MeshLambertMaterial({ color: 0x444a50 });
export const lampHeadMaterial = new THREE.MeshLambertMaterial({
  color: 0xf5f2da,
  emissive: 0x000000,
});
lampHeadMaterial.userData.noToon = true;

// Soft radial glow sprite shared by every lamp; opacity toggles with night
function makeGlowTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 64;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(32, 32, 2, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(255, 238, 170, 0.9)');
    gradient.addColorStop(0.4, 'rgba(255, 220, 120, 0.35)');
    gradient.addColorStop(1, 'rgba(255, 220, 120, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);
  }
  return new THREE.CanvasTexture(canvas);
}

const glowMaterial = new THREE.SpriteMaterial({
  map: makeGlowTexture(),
  transparent: true,
  opacity: 0,
  blending: THREE.AdditiveBlending,
  depthWrite: false,
});
glowMaterial.userData.noToon = true;

export function buildPropsIntoTile(tile: THREE.Group, rng: Rng): void {
  const count = Math.ceil(TILE_LENGTH / POLE_PITCH) * 2;
  const poles = new InstancedSet(poleGeo, poleMat, count);
  const heads = new InstancedSet(headGeo, lampHeadMaterial, count);
  heads.mesh.userData.noToon = true;

  let i = 0;
  for (let z = -rng.next() * POLE_PITCH; z > -TILE_LENGTH; z -= POLE_PITCH) {
    const side = i % 2 === 0 ? -1 : 1; // Alternate sides
    const x = side * 5.6; // Outer pavement edge
    poles.add(x, 1.3, z);
    heads.add(x, 2.62, z);
    // Glow halo (v1 streetLightGlow, opacity 0.7 at night / 0 by day)
    const glow = new THREE.Sprite(glowMaterial);
    glow.position.set(x, 2.62, z);
    glow.scale.setScalar(1.6);
    glow.userData.noToon = true;
    tile.add(glow);
    i++;
  }

  tile.add(poles.mesh, heads.mesh);
}

/** Night: lamp heads glow + halos appear (stage calls on time changes). */
export function setLampsLit(lit: boolean): void {
  lampHeadMaterial.emissive.setHex(lit ? 0xffee88 : 0x000000);
  lampHeadMaterial.emissiveIntensity = lit ? 1.2 : 1;
  glowMaterial.opacity = lit ? 0.7 : 0; // v1 glow halo values
}
